import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2,
  useTheme,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTextField from "../common/textFields/TextField";
import AppButton from "../common/buttons/AppButton";
import DatePicker2 from "../common/textFields/DatePicker2";
import CustomTypography from "../common/form/CustomTypography";
import { Dayjs } from "dayjs";
import { ShipmentRequest } from "../../../models/AddShipment";
import AppSearchDropDown2 from "../common/filter/AppSearchDropdown2";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { useNotification } from "../../../utils/NotificationsContext";

interface AddShipmentPopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ShipmentRequest) => void;
  handleDownloadTemplate: () => void;
  handleUploadCsv: (order_id: number, file: File) => void;
  onSuccess?: () => void;
  preSelectedOrderId?: number;
}

const AddShipmentPopup: React.FC<AddShipmentPopupProps> = ({
  open,
  onClose,
  onSubmit,
  handleDownloadTemplate,
  handleUploadCsv,
  onSuccess,
  preSelectedOrderId,
}) => {
  const theme = useTheme();
  const { shipmentStore } = useStores();
  const { showError } = useNotification();

  const [formData, setFormData] = useState<ShipmentRequest>({
    inventory_id: null,
    pack_ids: null,
    order_id: preSelectedOrderId || null,
    first_name: "",
    last_name: "",
    email: "",
    alternate_email_id: "",
    phone: "",
    alternate_phone: "",
    address1: "",
    address2: "",
    landmark: "",
    country: "",
    state: "",
    city: "",
    pincode: "",
    dispatch_date: null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = <T extends string | Dayjs | null>(
    field: keyof ShipmentRequest,
    value: T,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleTextFieldChange =
    (field: keyof ShipmentRequest) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (field === "dispatch_date") {
        // Don't allow text input for date field
        return;
      }
      handleInputChange(field, event.target.value);
    };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.order_id || formData.order_id === null) {
      newErrors.order_id = "Order ID is required" as string;
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (
      formData.alternate_email_id &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.alternate_email_id)
    ) {
      newErrors.alternate_email_id = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.address1.trim()) {
      newErrors.address1 = "Address is required";
    }

    if (!formData.country) {
      newErrors.country = "Country is required";
    }

    if (!formData.state) {
      newErrors.state = "State is required";
    }

    if (!formData.city) {
      newErrors.city = "City is required";
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pin code is required";
    }

    setErrors(newErrors as Record<string, string>);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
      handleClear();
      onClose();
      onSuccess?.();
    }
  };

  const handleClear = () => {
    setFormData({
      inventory_id: null,
      pack_ids: null,
      order_id: preSelectedOrderId || null,
      first_name: "",
      last_name: "",
      email: "",
      alternate_email_id: "",
      phone: "",
      alternate_phone: "",
      address1: "",
      address2: "",
      landmark: "",
      country: "",
      state: "",
      city: "",
      pincode: "",
      dispatch_date: null,
    });
    setErrors({});
  };

  let apiTimer: NodeJS.Timeout;

  const handleOrderSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        shipmentStore.getOrder(searchText);
      }, 1500);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          overflow: "visible",
        },
      }}
    >
      {/* Close button positioned half outside the modal for floating effect */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: -12,
          top: -12,
          color: theme.palette.customColors.white,
          backgroundColor: theme.palette.grey[500],
          borderRadius: "50%",
          width: "30px",
          height: "30px",
          "&:hover": {
            backgroundColor: theme.palette.grey[700],
          },
          zIndex: 1300,
        }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <CustomTypography variant="heading4" fontWeight={700}>
            Add Recipient Details
          </CustomTypography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <AppButton
              variant="primaryBlock"
              label="Download Template"
              onClick={() => {
                handleDownloadTemplate();
              }}
            />
            <AppButton
              variant="primaryBlock"
              label="Upload CSV"
              onClick={() => {
                const fileInput = document.createElement("input");
                fileInput.type = "file";
                fileInput.accept = ".csv";
                fileInput.onchange = (event) => {
                  const file = (event.target as HTMLInputElement).files?.[0];
                  if (file && formData.order_id) {
                    handleUploadCsv(formData.order_id!, file);
                  } else if (!formData.order_id) {
                    showError("Error", "Please select an order first");
                  }
                };
                fileInput.click();
              }}
            />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <hr style={{ color: "grey", width: "100%", height: "2px" }} />
        <Grid2 container spacing={3} mt={2}>
          {/* Personal Information Section */}
          <Grid2 size={12}>
            <CustomTypography variant="heading5" fontWeight={500}>
              Personal Information
            </CustomTypography>
          </Grid2>
          <Grid2 size={12}>
            <Box>
              <AppSearchDropDown2
                minWidth="180px"
                isMultiSelect={false}
                title="Search Order"
                value={
                  formData.order_id
                    ? [
                        {
                          id: String(formData.order_id),
                          value: String(formData.order_id),
                        },
                      ]
                    : []
                }
                fontSize="14px"
                list={shipmentStore.orderIds?.map((item) => ({
                  id: String(item.id),
                  value: String(item.value),
                }))}
                local={false}
                handleSearch={handleOrderSearch}
                handleSelect={(items: FilterValue[]) => {
                  setFormData((prev) => ({
                    ...prev,
                    order_id: Number(items[0]?.id) || null,
                  }));
                }}
                disabled={!!preSelectedOrderId}
              />
              {errors.order_id && (
                <CustomTypography
                  variant="body1"
                  color={theme.palette.customColors.lightred}
                  sx={{
                    fontSize: "0.75rem",
                    mt: 0.5,
                  }}
                >
                  {errors.order_id}
                </CustomTypography>
              )}
            </Box>
          </Grid2>

          {/* First Name and Last Name */}
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <CustomTextField
              placeholder="First Name"
              value={formData.first_name}
              onChange={handleTextFieldChange("first_name")}
              width="100%"
              error={!!errors.first_name}
            />
            {errors.first_name && (
              <CustomTypography
                variant="body1"
                color={theme.palette.customColors.lightred}
                sx={{
                  fontSize: "0.75rem",
                  mt: 0.5,
                }}
              >
                {errors.first_name}
              </CustomTypography>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <CustomTextField
              placeholder="Last Name"
              value={formData.last_name}
              onChange={handleTextFieldChange("last_name")}
              width="100%"
            />
          </Grid2>

          {/* Email Fields */}
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <CustomTextField
              placeholder="Email *"
              value={formData.email}
              onChange={handleTextFieldChange("email")}
              width="100%"
              error={!!errors.email}
            />
            {errors.email && (
              <CustomTypography
                variant="body1"
                color={theme.palette.customColors.lightred}
                sx={{
                  fontSize: "0.75rem",
                  mt: 0.5,
                }}
              >
                {errors.email}
              </CustomTypography>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <CustomTextField
              placeholder="Alternate Email ID"
              value={formData.alternate_email_id}
              onChange={handleTextFieldChange("alternate_email_id")}
              width="100%"
            />
          </Grid2>

          {/* Phone Fields */}
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <CustomTextField
              placeholder="Phone *"
              value={formData.phone}
              onChange={handleTextFieldChange("phone")}
              width="100%"
              error={!!errors.phone}
            />
            {errors.phone && (
              <CustomTypography
                variant="body1"
                color={theme.palette.customColors.lightred}
                sx={{
                  fontSize: "0.75rem",
                  mt: 0.5,
                }}
              >
                {errors.phone}
              </CustomTypography>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <CustomTextField
              placeholder="Alternate Phone"
              value={formData.alternate_phone}
              onChange={handleTextFieldChange("alternate_phone")}
              width="100%"
            />
          </Grid2>

          {/* Address Section */}
          <Grid2 size={12}>
            <CustomTypography variant="heading5" fontWeight={500}>
              Shipping Address
            </CustomTypography>
          </Grid2>

          {/* Shipping Address */}
          <Grid2 size={12}>
            <CustomTextField
              placeholder="Address 1 *"
              value={formData.address1}
              onChange={handleTextFieldChange("address1")}
              width="100%"
              error={!!errors.address1}
            />
            {errors.address1 && (
              <CustomTypography
                variant="body1"
                color={theme.palette.customColors.lightred}
                sx={{
                  fontSize: "0.75rem",
                  mt: 0.5,
                }}
              >
                {errors.address1}
              </CustomTypography>
            )}
          </Grid2>

          <Grid2 size={12}>
            <CustomTextField
              placeholder="Address 2"
              value={formData.address2}
              onChange={handleTextFieldChange("address2")}
              width="100%"
            />
          </Grid2>

          <Grid2 size={12}>
            <CustomTextField
              placeholder="Landmark"
              value={formData.landmark}
              onChange={handleTextFieldChange("landmark")}
              width="100%"
            />
          </Grid2>

          {/* Country, State, City */}
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
            <CustomTextField
              placeholder="Country *"
              value={formData.country}
              onChange={handleTextFieldChange("country")}
              width="100%"
              error={!!errors.country}
            />
            {errors.country && (
              <CustomTypography
                variant="body1"
                color={theme.palette.customColors.lightred}
                sx={{
                  fontSize: "0.75rem",
                  mt: 0.5,
                }}
              >
                {errors.country}
              </CustomTypography>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
            <CustomTextField
              placeholder="State *"
              value={formData.state}
              onChange={handleTextFieldChange("state")}
              width="100%"
              error={!!errors.state}
            />
            {errors.state && (
              <CustomTypography
                variant="body1"
                color={theme.palette.customColors.lightred}
                sx={{
                  fontSize: "0.75rem",
                  mt: 0.5,
                }}
              >
                {errors.state}
              </CustomTypography>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
            <CustomTextField
              placeholder="City *"
              value={formData.city}
              onChange={handleTextFieldChange("city")}
              width="100%"
              error={!!errors.city}
            />
            {errors.city && (
              <CustomTypography
                variant="body1"
                color={theme.palette.customColors.lightred}
                sx={{
                  fontSize: "0.75rem",
                  mt: 0.5,
                }}
              >
                {errors.city}
              </CustomTypography>
            )}
          </Grid2>

          {/* Pin Code and Dispatch Date */}
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <CustomTextField
              placeholder="Pin Code *"
              value={formData.pincode}
              onChange={handleTextFieldChange("pincode")}
              width="100%"
              error={!!errors.pincode}
            />
            {errors.pincode && (
              <CustomTypography
                variant="body1"
                color={theme.palette.customColors.lightred}
                sx={{
                  fontSize: "0.75rem",
                  mt: 0.5,
                }}
              >
                {errors.pincode}
              </CustomTypography>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <DatePicker2
              key={`dispatch-date`}
              label="Dispatch Date"
              value={null}
              onChange={(date: Dayjs | null) => {
                if (!date) return;
                const formattedDate = date?.format("YYYY-MM-DD HH:mm:ss");
                handleInputChange("dispatch_date", formattedDate);
              }}
              borderColor="#E0E0E0"
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: "center", gap: 2 }}>
        <AppButton
          variant="grayscale"
          label="Clear"
          onClick={handleClear}
          customStyles={{
            minWidth: "120px",
            height: "40px",
          }}
        />
        <AppButton
          variant="primaryBlock"
          label="Submit"
          onClick={handleSubmit}
          customStyles={{
            minWidth: "120px",
            height: "40px",
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default observer(AddShipmentPopup);
